import { useState } from "react";
import { useLlmConfig } from "../hooks/useLlmConfig";
import type { LlmProviderInfo, LlmTenantConfig } from "../hooks/useLlmConfig";
import { showApiError } from "@/lib/error-toast";
import styles from "./LlmSettingsPage.module.css";

const ROUTING_MODES = [
  { value: "off", label: "Off" },
  { value: "fallback", label: "Fallback on Low Confidence" },
  { value: "preferred", label: "LLM Preferred" },
  { value: "only", label: "LLM Only" },
];

const NEEDS_KEY_MSG = "Set an API key first";

// OCR rejects enable-without-key with { error: "api_key_required" }; that raw
// code would otherwise leak to the toast, so map it to the human message.
function apiKeyRequiredMessage(body: unknown): string | undefined {
  if (body && typeof body === "object" && (body as Record<string, unknown>).error === "api_key_required") {
    return NEEDS_KEY_MSG;
  }
  return undefined;
}

interface ProviderCardProps {
  provider: LlmProviderInfo;
  config: LlmTenantConfig | undefined;
  onUpdate: (provider: string, update: Record<string, unknown>) => Promise<void>;
  onValidate: (provider: string, apiKey: string) => Promise<boolean>;
  disabled?: boolean;
}

function statusLabel(enabled: boolean, maskedApiKey: string | undefined): string {
  if (!enabled) return 'Disabled';
  if (!maskedApiKey) return 'Needs API key';
  return 'Active';
}

function ProviderCard({ provider, config, onUpdate, onValidate, disabled }: ProviderCardProps) {
  const enabled = config?.enabled ?? false;
  const hasKey = Boolean(config?.maskedApiKey);
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [showKeyEntry, setShowKeyEntry] = useState(false);
  const [validationResult, setValidationResult] = useState<boolean | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  const inputsDisabled = disabled || !enabled;
  // Can't enable a provider with no key on file. An already-enabled
  // provider stays toggleable so the operator can turn it back off.
  const blockedNoKey = !disabled && !enabled && !hasKey;

  const handleToggle = async () => {
    try {
      await onUpdate(provider.name, { enabled: !enabled });
    } catch (err) {
      showApiError(err, { fallback: NEEDS_KEY_MSG, formatBody: apiKeyRequiredMessage });
    }
  };

  const handleValidate = async () => {
    if (!apiKey) return;
    setIsValidating(true);
    try {
      const valid = await onValidate(provider.name, apiKey);
      setValidationResult(valid);
    } catch {
      setValidationResult(false);
    } finally {
      setIsValidating(false);
    }
  };

  const handleSaveKey = async () => {
    if (!apiKey) return;
    await onUpdate(provider.name, { apiKey });
    setApiKey("");
    setValidationResult(null);
  };

  const handleBudgetBlur = async (field: string, value: string) => {
    const num = parseFloat(value);
    if (!isNaN(num)) {
      await onUpdate(provider.name, { [field]: num });
    }
  };

  return (
    <div className={styles.providerCard} data-testid={`provider-card-${provider.name}`}>
      <div className={styles.cardHeader}>
        <span className={styles.providerName}>{provider.displayName}</span>
        <span className={styles.statusPill} data-status={statusLabel(enabled, config?.maskedApiKey)}>
          {statusLabel(enabled, config?.maskedApiKey)}
        </span>
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={handleToggle}
            disabled={disabled || blockedNoKey}
            title={blockedNoKey ? NEEDS_KEY_MSG : undefined}
            data-testid={`toggle-${provider.name}`}
          />
          <span className={styles.toggleSlider} />
        </label>
      </div>

      {blockedNoKey && (
        <div className={styles.cardBody}>
          {!showKeyEntry ? (
            <button
              className={styles.btn}
              type="button"
              onClick={() => setShowKeyEntry(true)}
              data-testid={`add-key-${provider.name}`}
            >
              + Add API key
            </button>
          ) : (
            <div className={styles.fieldRow}>
              <span className={styles.fieldLabel}>API Key</span>
              <input
                type={showKey ? "text" : "password"}
                className={styles.fieldInput}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter API key"
                data-testid={`apikey-${provider.name}`}
              />
              <button
                className={styles.btn}
                onClick={() => setShowKey(!showKey)}
                type="button"
              >
                {showKey ? "Hide" : "Show"}
              </button>
              <button
                className={styles.btnPrimary}
                onClick={handleSaveKey}
                disabled={!apiKey}
                data-testid={`save-key-${provider.name}`}
              >
                Save Key
              </button>
            </div>
          )}
        </div>
      )}

      {enabled && (
        <div className={styles.cardBody}>
          {/* API Key */}
          <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>API Key</span>
            <input
              type={showKey ? "text" : "password"}
              className={styles.fieldInput}
              value={apiKey}
              onChange={(e) => {
                setApiKey(e.target.value);
                setValidationResult(null);
              }}
              placeholder={config?.maskedApiKey || "Enter API key"}
              disabled={inputsDisabled}
              data-testid={`apikey-${provider.name}`}
            />
            <button
              className={styles.btn}
              onClick={() => setShowKey(!showKey)}
              type="button"
              disabled={inputsDisabled}
            >
              {showKey ? "Hide" : "Show"}
            </button>
          </div>

          <div className={styles.btnRow}>
            <button
              className={styles.btn}
              onClick={handleValidate}
              disabled={inputsDisabled || !apiKey || isValidating}
              data-testid={`validate-${provider.name}`}
            >
              {isValidating ? "Validating..." : "Validate"}
            </button>
            <button
              className={styles.btnPrimary}
              onClick={handleSaveKey}
              disabled={inputsDisabled || !apiKey}
              data-testid={`save-key-${provider.name}`}
            >
              Save Key
            </button>
            {validationResult === true && (
              <span className={styles.validIcon} data-testid="validation-success">&#x2714;</span>
            )}
            {validationResult === false && (
              <span className={styles.invalidIcon} data-testid="validation-failure">&#x2718;</span>
            )}
          </div>

          {/* Model selector */}
          <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>Model</span>
            <select
              className={styles.fieldSelect}
              value={config?.modelId ?? ""}
              onChange={(e) => onUpdate(provider.name, { modelId: e.target.value })}
              disabled={inputsDisabled}
              data-testid={`model-${provider.name}`}
            >
              <option value="">Select a model</option>
              {provider.models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.tier})
                </option>
              ))}
            </select>
          </div>

          {/* Budgets */}
          <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>Daily Budget</span>
            <span className={styles.budgetPrefix}>$</span>
            <input
              type="number"
              className={styles.fieldInput}
              defaultValue={config?.dailyBudgetUsd ?? 0}
              onBlur={(e) => handleBudgetBlur("dailyBudgetUsd", e.target.value)}
              disabled={inputsDisabled}
              min={0}
              step={0.01}
              data-testid={`daily-budget-${provider.name}`}
            />
          </div>

          <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>Monthly Budget</span>
            <span className={styles.budgetPrefix}>$</span>
            <input
              type="number"
              className={styles.fieldInput}
              defaultValue={config?.monthlyBudgetUsd ?? 0}
              onBlur={(e) => handleBudgetBlur("monthlyBudgetUsd", e.target.value)}
              disabled={inputsDisabled}
              min={0}
              step={0.01}
              data-testid={`monthly-budget-${provider.name}`}
            />
          </div>

          {/* Default routing mode */}
          <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>Default Routing</span>
            <select
              className={styles.fieldSelect}
              value={config?.defaultRoutingMode ?? "off"}
              onChange={(e) => onUpdate(provider.name, { defaultRoutingMode: e.target.value })}
              disabled={inputsDisabled}
              data-testid={`routing-${provider.name}`}
            >
              {ROUTING_MODES.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          {/* Fallback threshold */}
          <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>Fallback Threshold</span>
            <div className={styles.sliderRow}>
              <input
                type="range"
                className={styles.slider}
                min={0}
                max={1}
                step={0.05}
                value={config?.fallbackConfidenceThreshold ?? 0.7}
                onChange={(e) =>
                  onUpdate(provider.name, {
                    fallbackConfidenceThreshold: parseFloat(e.target.value),
                  })
                }
                disabled={inputsDisabled}
                data-testid={`threshold-${provider.name}`}
              />
              <span className={styles.sliderValue}>
                {(config?.fallbackConfidenceThreshold ?? 0.7).toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function LlmSettingsPage() {
  const { providers, isLoading, error, updateConfig, validateKey, getConfigFor } =
    useLlmConfig();

  const handleUpdate = async (provider: string, update: Record<string, unknown>) => {
    await updateConfig(provider, update);
  };

  if (isLoading) {
    return (
      <div className={`${styles.page} form-compact`}>
        <div className={styles.loadingWrap}>
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.page} form-compact`}>
      <div className={styles.header}>
        <h1 className={styles.title}>LLM Providers</h1>
        <p className={styles.subtitle}>Configure AI-powered OCR enhancement</p>
      </div>

      <div className={styles.llmIntentBanner} role="status">
        Skarn manages which LLM providers are available for your plan.
        Contact your Skarn admin to enable additional providers.
      </div>

      {error === "encryption_not_configured" && (
        <div className={styles.warningBanner} data-testid="encryption-warning">
          Encryption is not configured on the OCR service. API keys cannot be stored securely.
        </div>
      )}

      {providers.map((provider) => (
        <ProviderCard
          key={provider.name}
          provider={provider}
          config={getConfigFor(provider.name)}
          onUpdate={handleUpdate}
          onValidate={validateKey}
          disabled={error === "encryption_not_configured"}
        />
      ))}

      {!isLoading && providers.length === 0 && (
        <div className="tab-placeholder">
          <span className="tab-placeholder-text">No LLM providers available</span>
        </div>
      )}
    </div>
  );
}
